import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "./Styles/Color.css";
import { NotificationProvider } from "./Context/NotificationContext";
import { WebSocketProvider } from "./Context/WebSocketContext";
import { BranchProvider } from "./Context/BranchContext";
import { CircularProgress, Box } from "@mui/material";

const isLoggedIn = () => Boolean(localStorage.getItem("token"));

// Redirect to dashboard if already authenticated
const PublicRoute = ({ children }) =>
  isLoggedIn() ? <Navigate to="/dashboard" replace /> : children;

// Redirect to login if not authenticated
const PrivateRoute = ({ children }) =>
  isLoggedIn() ? children : <Navigate to="/" replace />;

const Login = lazy(() => import("./Auth/Login"));
const Profile = lazy(() => import("./Auth/Profile.js"));
const Dashboard = lazy(() => import("./Home/Dashboard/Dashboard"));
const StaffManagement = lazy(() => import("./Home/Staffs/StaffManagement"));
const CustomerManagement = lazy(() => import("./Home/Customers/CustomerManagement"));
const StaffAttendance = lazy(() => import("./Home/Staffs/StaffAttendance"));
const BranchManagement = lazy(() => import("./Home/Branch/BranchManagement"));
const CategoryManagement = lazy(() => import("./Home/Category/CategoryManagement"));
const BannerManagement = lazy(() => import("./Home/Banner/BannerManagement"));
const CampaignManagement = lazy(() => import("./Home/Campaign/CampaignManagement"));
const CheckoutOffer = lazy(() => import("./Home/Campaign/CheckoutOffer"));
const ReferralManagement = lazy(() => import("./Home/Campaign/ReferralManagement"));
const OrderManagement = lazy(() => import("./Home/Order/OrderManagement"));
const OrderScan = lazy(() => import("./Home/Order/OrderScan"));
const VendorBillPrint = lazy(() => import("./Views/Print/VendorBillPrint"));
const CustomerBillPrint = lazy(() => import("./Views/Print/CustomerBillPrint"));
const ProductsManagement = lazy(() => import("./Home/Products/ProductsManagement"));
const ProductApprovals = lazy(() => import("./Home/Products/ProductApprovals"));
const VendorManagement = lazy(() => import("./Home/Vendor/Vendor"));
const PaymentManagement = lazy(() => import("./Home/Payment/PaymentManagement"));
const PaymentRecovery = lazy(() => import("./Home/Payment/PaymentRecovery"));
const SettlementManagement = lazy(() => import("./Home/Settlement/SettlementManagement"));
const SalaryManagement = lazy(() => import("./Home/Salary/SalaryManagement"));
const CredentialManagement = lazy(() => import("./Home/Credential/CredentialManagement"));
const PasswordResetRequests = lazy(() => import("./Home/Credential/PasswordResetRequests"));
const ReportsManagement = lazy(() => import("./Home/Reports/ReportsManagement"));
const ExpenseManagement = lazy(() => import("./Home/Expense/ExpenseManagement"));
const RatingManagement = lazy(() => import("./Home/Ratings/RatingManagement"));

const PageLoader = () => (
  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
    <CircularProgress sx={{ color: "var(--pink-40)" }} />
  </Box>
);

function App() {
  return (
    <NotificationProvider>
    <BranchProvider>
    <WebSocketProvider>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/staff-management" element={<PrivateRoute><StaffManagement /></PrivateRoute>} />
          <Route path="/customer-management" element={<PrivateRoute><CustomerManagement /></PrivateRoute>} />
          <Route path="/staff-Attendance" element={<PrivateRoute><StaffAttendance /></PrivateRoute>} />
          <Route path="/Profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
          <Route path="/branch-management" element={<PrivateRoute><BranchManagement /></PrivateRoute>} />
          <Route path="/category-management" element={<PrivateRoute><CategoryManagement /></PrivateRoute>} />
          <Route path="/banner-management" element={<PrivateRoute><BannerManagement /></PrivateRoute>} />
          <Route path="/campaigns-management" element={<PrivateRoute><CampaignManagement /></PrivateRoute>} />
          <Route path="/checkout-offer" element={<PrivateRoute><CheckoutOffer /></PrivateRoute>} />
          <Route path="/referral-management" element={<PrivateRoute><ReferralManagement /></PrivateRoute>} />
          <Route path="/vendor-management" element={<PrivateRoute><VendorManagement /></PrivateRoute>} />
          <Route path="/order-management" element={<PrivateRoute><OrderManagement /></PrivateRoute>} />
          <Route path="/order-scan" element={<PrivateRoute><OrderScan /></PrivateRoute>} />
          <Route path="/print/vendor-bill/:orderId" element={<PrivateRoute><VendorBillPrint /></PrivateRoute>} />
          <Route path="/print/customer-bill/:orderId" element={<PrivateRoute><CustomerBillPrint /></PrivateRoute>} />
          <Route path="/products-management" element={<PrivateRoute><ProductsManagement /></PrivateRoute>} />
          <Route path="/product-approvals" element={<PrivateRoute><ProductApprovals /></PrivateRoute>} />
          <Route path="/payment-management" element={<PrivateRoute><PaymentManagement /></PrivateRoute>} />
          <Route path="/payment-recovery" element={<PrivateRoute><PaymentRecovery /></PrivateRoute>} />
          <Route path="/settlement-management" element={<PrivateRoute><SettlementManagement /></PrivateRoute>} />
          <Route path="/salary-management" element={<PrivateRoute><SalaryManagement /></PrivateRoute>} />
          <Route path="/credential-management" element={<PrivateRoute><CredentialManagement /></PrivateRoute>} />
          <Route path="/password-reset-requests" element={<PrivateRoute><PasswordResetRequests /></PrivateRoute>} />
          <Route path="/reports" element={<PrivateRoute><ReportsManagement /></PrivateRoute>} />
          <Route path="/expense-management" element={<PrivateRoute><ExpenseManagement /></PrivateRoute>} />
          <Route path="/rating-management" element={<PrivateRoute><RatingManagement /></PrivateRoute>} />
          <Route path="*" element={<Navigate to={isLoggedIn() ? "/dashboard" : "/"} replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </WebSocketProvider>
    </BranchProvider>
    </NotificationProvider>
  );
}

export default App;